import { Card, Form } from 'antd';
import DisplayAlert from '../../../../components/Commons/Alert';
import { ReactNode, useEffect, useState } from 'react';
import { getAll, update as bookingUpdate, updateStatus } from '../../../../services/bookings.service';
import { AlertService } from '../../../../services/alert.service';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import ModalPending from './Modal';
import TablePending from './Table';
import FilterPending from './Filter';
import { create as bookingServiceCreate } from '../../../../services/bookingServices.service';
import { getAll as loadServiceStates } from '../../../../services/serviceStates.service';

export default function ListPending(props: any) {
    const [perPage, setPerPage] = useState<number | undefined>(10);
    const [order, setOrder] = useState<string>('created_at-desc');
    const [total, setTotal] = useState<number>(0);
    const [bookings, setBookings] = useState([]);
    const [currentPage, setCurrentPage] = useState<number | undefined>(1);
    const [search, setSearch] = useState<string>('');
    const [cancelBooking, setCancelBooking] = useState<any | undefined>(undefined);
    const [approveBooking, setApproveBooking] = useState<any | undefined>(undefined);
    const [styleResults, setStyleResults] = useState<any | undefined>(props.styleResults ?? undefined);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [status, setStatus] = useState<string>('');
    const [filters, setFilters] = useState<ReactNode>();

    const [formSearch] = Form.useForm();
    const [formFilter] = Form.useForm();
    const [formCancelBooking] = Form.useForm();

    const navigate = useNavigate();
    const { tab } = useParams();

    const { isLoading: bookingsLoading, refetch: loadBookings } = useQuery(
        ['loadBookings', currentPage, search, order, perPage, startDate, endDate],
        () => getAll({
            page: currentPage, per_page: perPage, order, search, status: 'pending',
            start_date: startDate, end_date: endDate, created_by: 'operator'
        }),
        {
            onSuccess: response => {
                setBookings(response.data.map((booking: any) => {
                    return {
                        key: booking.data.id,
                        ...booking.data,
                        ...booking.relationships
                    }
                }));

                setCurrentPage(response.meta.current_page);
                setPerPage(response.meta.per_page);
                setTotal(response.meta.total);
            },
            refetchOnWindowFocus: false
        }
    );

    const { data: serviceStates } = useQuery(
        ['loadServiceStates'],
        () => loadServiceStates(),
        { refetchOnWindowFocus: false }
    );

    const { mutate: cancelBookingMutate } = useMutation(updateStatus,
        {
            onSuccess: () => {
                AlertService.sendAlert([
                    {
                        text: `A reserva ${cancelBooking?.id} foi rejeitada.`,
                        type: 'warning',
                        nextPage: true
                    }
                ]);
                setCancelBooking(undefined);
                navigate('/bookings/bookingsToApprove/rejected/update')
            }
        }
    );

    const { mutate: updateBooking, isLoading: updateBookingLoading } = useMutation(bookingUpdate, {
        onSuccess: () => {
            AlertService.sendAlert([
                {
                    text: `Reserva N° ${approveBooking.id} aprovada com sucesso.`,
                    nextPage: true
                }
            ]);

            navigate(`/bookings/bookings/${approveBooking.id}/edit`);
        }
    });

    const { mutate: createService, isLoading: createServiceLoading } = useMutation(bookingServiceCreate, {
        onSuccess: () => {
            updateBooking({
                id: approveBooking.id,
                values: {
                    status: 'approved',
                    emphasis: true,
                    deposits_paid: approveBooking.deposits_paid || 0
                }
            });
        }
    });

    const onSubmitCancelBooking = () => {
        formCancelBooking
            .validateFields()
            .then(values => cancelBookingMutate({
                values: { ...values, status: 'refused' }, id: cancelBooking?.id
            }))
            .catch(() => null);
    }

    const onSubmitApproveBooking = () => {
        createService({
            start: approveBooking.start_date,
            hour: approveBooking.hour,
            car_type: '',
            staff_id: null,
            supplier_id: null,
            pickup_location: approveBooking.pickup_location,
            pickup_location_id: approveBooking.pickup_location_id,
            pickup_zone_id: approveBooking?.pickupLocation?.data?.zone_id || null,
            pickup_zone: approveBooking?.pickupLocation?.relationships?.zone?.name || null,
            pickup_address: approveBooking?.pickupLocation?.data?.address || null,
            pickup_reference_point: approveBooking?.pickupLocation?.data?.reference_point || null,
            dropoff_location: approveBooking.dropoff_location,
            dropoff_location_id: approveBooking.dropoff_location_id,
            dropoff_zone_id: approveBooking?.dropoffLocation?.data?.zone_id || null,
            dropoff_zone: approveBooking?.dropoffLocation?.relationships?.zone?.name || null,
            dropoff_address: approveBooking?.dropoffLocation?.data?.address || null,
            dropoff_reference_point: approveBooking?.dropoffLocation?.data?.reference_point || null,
            service_state_id: serviceStates?.data?.map((item: any) => item.data)
                .find((state: any) => state.is_default)?.id,
            service_type_id: approveBooking.service_type_id,
            booking_id: approveBooking.id,
            emphasis: true,
            value: approveBooking.value,
            number_adults: approveBooking.pax_group,
            number_children: 0,
            number_baby_chair: 0,
            booster: 0,
            charge: 'operator',
            notes: approveBooking.additional_information,
        });
    }

    useEffect(() => {
        formFilter.setFieldsValue({ filter_start_date: '', filter_end_date: '', filter_status: '' });
    }, []);

    useEffect(() => {
        if (!startDate) {
            formFilter.setFieldValue('filter_start_date', '');
        }

        if (!endDate) {
            formFilter.setFieldValue('filter_end_date', '');
        }

        formFilter.setFieldValue('filter_status', status);
    }, [startDate, endDate, status]);

    const filterProps = {
        formSearch,
        setSearch,
        formFilter,
        startDate,
        endDate,
        status,
        setCurrentPage,
        setStartDate,
        setEndDate,
        setStatus,
        setFilters
    }

    const tableProps = {
        setApproveBooking,
        setCancelBooking,
        setCurrentPage,
        setPerPage,
        setOrder,
        bookings,
        currentPage,
        bookingsLoading,
        search,
        startDate,
        endDate,
        status,
        perPage,
        order,
        total,
        styleResults,
        filters,
        hideFilter: props.hideFilter
    }

    const modalProps = {
        cancelBooking,
        approveBooking,
        onSubmitCancelBooking,
        onSubmitApproveBooking,
        setCancelBooking,
        setApproveBooking,
        formCancelBooking,
        loadingSubmit: createServiceLoading || updateBookingLoading
    }

    return (
        <>
            {tab === 'pendent' && <DisplayAlert />}
            <Card bodyStyle={{ padding: 8 }}>
                {!props.hideFilter && <FilterPending {...filterProps} />}
                <div className="table">
                    <TablePending {...tableProps} />
                    <ModalPending {...modalProps} />
                </div>
            </Card>
        </>
    );
}